import { useState, useEffect } from "react"
import { useDebounce } from "../../../hooks/useDebounce"
import MTable from "../../../components/mtable"
import InitialsAvatar from "../../../components/InitialsAvatar"
import { formatEnum } from "../../../utils/strings"
import { date } from "../../../utils/date"

const STATUS_FILTERS = [
    { label: 'All', value: undefined },
    { label: 'Pending Questionnaire', value: 'PendingQuestionnaire' },
    { label: 'Waiting For Match', value: 'WaitingForMatch' },
    { label: 'Preview', value: 'Preview' },
    { label: 'Match Proposed', value: 'MatchProposed' },
    { label: 'Match Confirmed', value: 'MatchConfirmed' },
]

function statusChipClass(status) {
    if (status === 'MatchConfirmed') return 'primary-dark-chip'
    if (status === 'WaitingForMatch') return 'neutral-chip'
    if (status === 'MatchProposed' || status === 'Preview') return 'primary-light-chip'
    return 'neutral-chip'
}

const MAPPER = { columns: [
    {
        label: 'Name', width: '2fr',
        render: (item) => (
            <div className="match-participant">
                <InitialsAvatar firstName={item.user.firstName} lastName={item.user.lastName} />
                <div style={{ minWidth: 0 }}>
                    <p className="card-label medium ellipsis">{item.user.firstName} {item.user.lastName}</p>
                    <p className="card-label-s neutral ellipsis">{item.user.email}</p>
                </div>
            </div>
        )
    },
    {
        label: 'Role / Track', width: '1fr',
        value: (item) => item.chosenTracks?.[0]?.trackName ? `${item.type} · ${item.chosenTracks?.[0]?.trackName}` : item.type
    },
    {
        label: 'Status', width: '1fr', align: 'center',
        render: (item) => (
            <div className="flex justify-center">
                <span className={`chip transparent ${statusChipClass(item.status)}`}>{formatEnum(item.status)}</span>
            </div>
        )
    },
    {
        label: 'Joined', width: '1fr', align: 'center',
        value: (item) => item.enrolledAt ? date(item.enrolledAt) : '—'
    },
    {
        label: 'Last Active', width: '1fr', align: 'center',
        value: (item) => item.lastActiveAt ? date(item.lastActiveAt) : '—'
    },
]}

export default function Participants({ programId }) {
    const [search, setSearch] = useState('')
    const [typeFilter, setTypeFilter] = useState(undefined)
    const [statusFilter, setStatusFilter] = useState(undefined)
    const [params, setParams] = useState({})
    const debouncedSearch = useDebounce(search, 250)

    useEffect(() => {
        setParams({
            ...(debouncedSearch ? { search: debouncedSearch } : {}),
            ...(typeFilter ? { type: typeFilter } : {}),
            ...(statusFilter ? { status: statusFilter } : {})
        })
    }, [debouncedSearch, typeFilter, statusFilter])

    return (
        <div>
            <div className="flex center space-between v-spacer-s">
                <div>
                    <p className="card-title">Participants</p>
                    <p className="neutral card-label">Browse mentors and mentees.</p>
                </div>
                <div className="card padding-xxs flex center gap-xs">
                    <button className={typeFilter === undefined ? 'solid' : 'outline'} onClick={() => setTypeFilter(undefined)}>All</button>
                    <button className={typeFilter === 'Mentor' ? 'solid' : 'outline'} onClick={() => setTypeFilter('Mentor')}>Mentors</button>
                    <button className={typeFilter === 'Mentee' ? 'solid' : 'outline'} onClick={() => setTypeFilter('Mentee')}>Mentees</button>
                </div>
            </div>

            <div className="card input-wrapper v-spacer-s">
                <span className="material-symbols-outlined icon input-icon">search</span>
                <input
                    placeholder="Search by name or email…"
                    value={search}
                    className="w-100"
                    onChange={(e) => setSearch(e.target.value)}
                />
            </div>

            <div className="card flex gap-xs wrap v-spacer-s">
                {STATUS_FILTERS.map(f => (
                    <button
                        key={f.label}
                        className={`chip ${statusFilter === f.value ? 'solid' : 'outline'}`}
                        onClick={() => setStatusFilter(f.value)}
                    >
                        {f.label}
                    </button>
                ))}
            </div>

            <MTable
                path={`programs/${programId}/participants`}
                queryParams={params}
                mapper={MAPPER}
            />
        </div>
    )
}